import { decrypt, useAES } from "@/utils/AES";
import { Environment } from "@/constants/env";

declare const __PROJECT_VERSION__: string;

let aegis: any = null;

const errorQueue: any[] = [];
// 监控只在非开发环境开启
export const IS_ENABLE_MONITOR = import.meta.env.VITE_ENV !== Environment.development;

/**
 * 预先设置全局错误监听，用于捕获 SDK 加载前的错误
 * 放在文件顶层立即执行
 */
if (IS_ENABLE_MONITOR && typeof window !== "undefined") {
  // 捕获脚本错误
  const originalOnerror = window.onerror;
  window.onerror = function (message, source, lineno, colno, error) {
    // 如果 Aegis 未初始化，存入队列
    if (!aegis) {
      errorQueue.push({
        type: "script",
        data: { message, source, lineno, colno, error }
      });
    }
    // 保持原有行为
    if (originalOnerror) {
      return originalOnerror.apply(window, arguments as any);
    }
  };

  // 捕获 Promise 未处理异常
  const originalOnUnhandledRejection = window.onunhandledrejection;
  window.onunhandledrejection = function (event) {
    if (!aegis) {
      errorQueue.push({
        type: "promise",
        data: event.reason
      });
    }
    if (originalOnUnhandledRejection) {
      originalOnUnhandledRejection.call(window, event);
    }
  };
}

/**
 * 初始化监控系统 (Aegis)
 */
export async function initializeMonitoring() {
  if (!IS_ENABLE_MONITOR) return;
  if (aegis) return; // 防止重复初始化

  try {
    // 1. 异步加载 Aegis SDK
    const AegisModule = await import("aegis-web-sdk");
    const Aegis = AegisModule.default;

    aegis = new Aegis({
      id: "nGR3XhKElVQlw0E4xj",
      reportApiSpeed: IS_ENABLE_MONITOR,
      reportAssetSpeed: IS_ENABLE_MONITOR,
      spa: IS_ENABLE_MONITOR,
      hostUrl: "https://rumt-zh.com",
      env: import.meta.env.VITE_ENV as Environment,
      apiDetail: IS_ENABLE_MONITOR,
      websocketHack: IS_ENABLE_MONITOR,
      version: __PROJECT_VERSION__,
      /**
       * 钩子函数：在上报前拦截
       */
      beforeReport(log: any) {
        // 过滤百度统计相关的错误
        if (log?.msg && typeof log.msg === 'string' && (log.msg.includes('hm.baidu.com') || log.msg.includes('hm.js'))) {
          return false;
        }
        return log;
      },
      beforeReportSpeed(log: any) {
        // 过滤百度统计相关资源测速
        if (log?.url && typeof log.url === 'string' && (log.url.includes('hm.baidu.com') || log.url.includes('hm.js'))) {
          return false;
        }
        return log;
      },
      api: {
        retCodeHandler(data: string, _url: string, _xhr: XMLHttpRequest) {
          try {
            data = JSON.parse(data);
          } catch (e) {}

          if (useAES && typeof data === "string") {
            data = decrypt(data);
          }

          return {
            isErr: ![0, 90000, 90001, 90002].includes((data as any).code),
            code: (data as any).code
          };
        }
      }
    });

    // 2. 上报队列中的历史错误
    while (errorQueue.length > 0) {
      const item = errorQueue.shift();
      if (item.type === "script") {
        const { message, source, lineno, colno, error } = item.data;
        // 手动构造错误上报
        aegis.error(`Script Error: ${message}\nLocation: ${source}:${lineno}:${colno}\nStack: ${error?.stack || 'N/A'}`);
      } else if (item.type === "promise") {
        aegis.error(item.data); // Promise rejection reason
      }
    }

  } catch (error) {
    console.error("监控初始化失败:", error);
  }
}

export { aegis };
